import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Atom, Warning, CheckCircle, Copy, Flask } from '@phosphor-icons/react';
import { safeCopy } from '../lib/safeCopy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_CLS = {
  high:   'bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]',
  medium: 'bg-[#FEF3C7] border-[#F3CC64] text-[#723B13]',
  low:    'bg-[#E1EFFE] border-[#A4CAFE] text-[#1E429F]',
  none:   'bg-[#DEF7EC] border-[#31C48D] text-[#03543F]',
};

const CRAMER_CLS = {
  I:   'bg-[#DEF7EC] border-[#31C48D] text-[#03543F]',
  II:  'bg-[#FEF3C7] border-[#F3CC64] text-[#723B13]',
  III: 'bg-[#FDE8E8] border-[#F8B4B4] text-[#9B1C1C]',
};

/** Structure Lookup widget — calls /api/structure/lookup, /read-across and /qsar.
 * Read-across and QSAR run server-side; this widget just displays the payloads. */
export default function StructureLookupWidget({ compact = false }) {
  const [query, setQuery] = useState('');
  const [method, setMethod] = useState('name');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lookup, setLookup] = useState(null);
  const [readAcross, setReadAcross] = useState(null);
  const [qsar, setQsar] = useState(null);

  const run = async () => {
    if (!query.trim()) return;
    setLoading(true); setError('');
    setLookup(null); setReadAcross(null); setQsar(null);
    try {
      // Fire all 3 backend calls in parallel — lookup is the identity step,
      // read-across + QSAR are pure backend analyses.
      const [lkup, ra, q] = await Promise.allSettled([
        axios.post(`${API_URL}/api/structure/lookup`, { query, method }, { withCredentials: true }),
        axios.post(`${API_URL}/api/structure/read-across`, { query, method, max_analogs: 5 }, { withCredentials: true }),
        axios.post(`${API_URL}/api/structure/qsar`, { query, method }, { withCredentials: true }),
      ]);
      if (lkup.status === 'fulfilled') setLookup(lkup.value.data);
      if (ra.status === 'fulfilled') setReadAcross(ra.value.data);
      if (q.status === 'fulfilled') setQsar(q.value.data);
      if (lkup.status === 'rejected' && ra.status === 'rejected' && q.status === 'rejected') {
        setError('Compound not found via PubChem or Aᴳ library.');
      }
    } catch (e) { setError('Lookup failed'); }
    finally { setLoading(false); }
  };

  const copy = (text) => { if (text) safeCopy(text); };

  return (
    <div className={`card-flat ${compact ? 'p-4' : 'p-5'}`} data-testid="structure-lookup-widget">
      <div className="flex items-center gap-2 mb-3">
        <Atom size={20} className="text-[#111827]" weight="duotone" />
        <h3 className="font-semibold text-sm text-[#111827]">Structure Lookup · Read-Across · QSAR</h3>
      </div>

      <div className="flex gap-2 mb-3">
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="h-9 text-xs rounded w-24" data-testid="structure-method-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="cas">CAS</SelectItem>
            <SelectItem value="smiles">SMILES</SelectItem>
          </SelectContent>
        </Select>
        <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="e.g., Linalool / 123-31-9 / CCO"
          className="h-9 text-xs rounded flex-1" data-testid="structure-query-input"
          onKeyDown={e => e.key === 'Enter' && run()} />
        <Button onClick={run} disabled={loading || !query.trim()} className="btn-primary h-9 text-xs rounded" data-testid="structure-run-btn">
          {loading ? 'Running...' : 'Run'}
        </Button>
      </div>

      {error && <p className="text-xs text-[#9B1C1C] mb-2">{error}</p>}

      {(lookup || readAcross || qsar) && (
        <div className="space-y-4">
          {/* Lookup */}
          {lookup && (
            <div className="border border-gray-200 rounded p-3" data-testid="structure-lookup-result">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2">PubChem · Identity</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-[#4B5563]">IUPAC:</span> {lookup.iupac_name || '—'}</div>
                <div><span className="text-[#4B5563]">Formula:</span> <span className="font-mono">{lookup.molecular_formula || '—'}</span></div>
                <div><span className="text-[#4B5563]">MW:</span> <span className="tabular-nums">{lookup.molecular_weight || '—'}</span></div>
                <div><span className="text-[#4B5563]">CID:</span> <span className="font-mono">{lookup.cid || '—'}</span></div>
              </div>
              {lookup.canonical_smiles && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="text-[10px] text-[#4B5563] uppercase tracking-wider">SMILES:</span>
                  <code className="text-[11px] font-mono bg-gray-50 px-1.5 py-0.5 rounded border border-gray-200 flex-1 break-all">{lookup.canonical_smiles}</code>
                  <button onClick={() => copy(lookup.canonical_smiles)} className="p-1 text-[#4B5563] hover:text-[#111827]" title="Copy">
                    <Copy size={12} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* QSAR */}
          {qsar && !qsar.error && (
            <div className="border border-gray-200 rounded p-3" data-testid="structure-qsar-result">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2">QSAR · Structural Alerts + Cramer TTC</p>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${CRAMER_CLS[qsar.cramer_class] || CRAMER_CLS.II}`} data-testid="qsar-cramer-class">
                  Cramer Class {qsar.cramer_class}
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${SEVERITY_CLS[qsar.overall_concern] || SEVERITY_CLS.none}`} data-testid="qsar-concern">
                  {qsar.alert_count} alert{qsar.alert_count === 1 ? '' : 's'} · {qsar.overall_concern}
                </span>
                <span className="text-[10px] text-[#4B5563]">
                  TTC oral <span className="font-bold tabular-nums">{qsar.ttc_oral_ug_per_person_day} µg/person/day</span>
                </span>
              </div>
              <p className="text-[11px] text-[#4B5563] mb-2">{qsar.interpretation}</p>
              {qsar.alerts?.length > 0 ? (
                <div className="space-y-1">
                  {qsar.alerts.map(a => (
                    <div key={a.alert} className="flex items-start gap-1.5 text-[11px] p-2 bg-gray-50 rounded border border-gray-200">
                      <Warning size={12} weight="fill" className={`mt-0.5 flex-shrink-0 ${a.severity === 'high' ? 'text-[#9B1C1C]' : a.severity === 'medium' ? 'text-[#D97706]' : 'text-[#4B5563]'}`} />
                      <div>
                        <span className="font-semibold text-[#111827]">{a.alert}</span>
                        <span className={`ml-1 text-[9px] font-bold uppercase ${a.severity === 'high' ? 'text-[#9B1C1C]' : a.severity === 'medium' ? 'text-[#D97706]' : 'text-[#4B5563]'}`}>· {a.severity}</span>
                        <p className="text-[#4B5563] mt-0.5">{a.rationale}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-[#03543F] flex items-center gap-1"><CheckCircle size={12} weight="fill" /> No structural alerts detected.</p>
              )}
            </div>
          )}

          {/* Read-Across */}
          {readAcross && (
            <div className="border border-gray-200 rounded p-3" data-testid="structure-readacross-result">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#4B5563] mb-2">Read-Across · Aᴳ DB Analogs</p>

              {/* Consensus badges */}
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-100 border border-gray-200 text-[#111827] tabular-nums">
                  {readAcross.consensus.n_analogs} analog{readAcross.consensus.n_analogs === 1 ? '' : 's'}
                </span>
                {readAcross.consensus.protective_noael != null && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#DEF7EC] border border-[#31C48D] text-[#03543F] tabular-nums">
                    Protective NOAEL: {readAcross.consensus.protective_noael} mg/kg
                  </span>
                )}
                {readAcross.consensus.any_genotoxic && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDE8E8] border border-[#F8B4B4] text-[#9B1C1C]">Analog genotoxic</span>
                )}
                {readAcross.consensus.any_mutagenic && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDE8E8] border border-[#F8B4B4] text-[#9B1C1C]">Analog mutagenic</span>
                )}
                {readAcross.consensus.any_eu_banned && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-[#FDE8E8] border border-[#F8B4B4] text-[#9B1C1C]">Analog EU banned</span>
                )}
                {readAcross.consensus.strongest_sensitiser !== 'None' && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-[#FEF3C7] border border-[#F3CC64] text-[#723B13]">
                    Strongest sens: {readAcross.consensus.strongest_sensitiser}
                  </span>
                )}
              </div>

              {readAcross.analogs.length === 0 ? (
                <p className="text-[11px] text-[#4B5563]">{readAcross.consensus.notes}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-1.5 px-2 font-semibold text-[#4B5563]">INCI</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#4B5563]">CAS</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#4B5563]">Class</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-[#4B5563]">MW</th>
                        <th className="text-right py-1.5 px-2 font-semibold text-[#4B5563]">NOAEL</th>
                        <th className="text-left py-1.5 px-2 font-semibold text-[#4B5563]">Flags</th>
                      </tr>
                    </thead>
                    <tbody>
                      {readAcross.analogs.map(a => (
                        <tr key={a.inci_name} className="border-b border-gray-100">
                          <td className="py-1.5 px-2 font-semibold text-[#111827]">
                            <Flask size={10} className="inline text-[#4B5563] mr-1" />{a.inci_name}
                          </td>
                          <td className="py-1.5 px-2 font-mono text-[#4B5563]">{a.cas_number || '—'}</td>
                          <td className="py-1.5 px-2 text-[#4B5563]">{a.function_class || '—'}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-[#4B5563]">{a.molecular_weight || '—'}</td>
                          <td className="py-1.5 px-2 text-right tabular-nums text-[#111827]">{a.noael ?? '—'}</td>
                          <td className="py-1.5 px-2">
                            <div className="flex gap-1 flex-wrap">
                              {a.genotoxicity && <span className="text-[9px] font-bold px-1 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">GEN</span>}
                              {a.mutagenicity && <span className="text-[9px] font-bold px-1 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">MUT</span>}
                              {a.eu_annex_ii && <span className="text-[9px] font-bold px-1 bg-[#FDE8E8] text-[#9B1C1C] border border-[#F8B4B4] rounded">EU-II</span>}
                              {a.skin_sensitization && !['None', 'Not classified'].includes(a.skin_sensitization) && (
                                <span className="text-[9px] font-bold px-1 bg-[#FEF3C7] text-[#723B13] border border-[#F3CC64] rounded">{a.skin_sensitization}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-[#4B5563] mt-2">{readAcross.consensus.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
